/**
 * チバリヨ - P4 顧客マスタ管理画面 (Phase 2-A-2)
 *
 * Phase 2-A-1 で実装済:
 *   - クライアント一覧の取得・表示
 *   - 状態セグメント / 検索 / クイックフィルタ / ソート / 統計バー
 *   - 顧客カード一覧(クリック選択)
 *
 * Phase 2-A-2 で追加(本セッション):
 *   - 全国市町村マスタの読み込み(public/data/municipalities.json)
 *   - 右ペインの編集フォーム(直接編集型)
 *   - 市町村サジェスト(緩やかモード = 案Q)
 *       - prefecture から municipality を絞り込み
 *       - municipality からの逆引きにも対応(「佐賀県基山町」形式で表示)
 *   - 保存処理 + バリデーション + 未保存検知(beforeunload + カード切り替え)
 *   - +新規追加モーダル(編集フォーム流用、Phase 2-A-3 から前倒し)
 *
 * Phase 2-A-3 で実装予定:
 *   - アーカイブ / 復活モーダル
 *   - 統合候補表示の精緻化
 *   - CSV インポート(256社投入準備)
 *
 * 設計メモ:
 *   - admin only(viewer / sales には main.js のナビ表示で非表示)
 *   - クライアントサイドフィルタ/ソート(256件程度なので全件メモリ展開で十分)
 *   - フィルタ状態は filterState オブジェクトで一元管理
 *   - 編集状態は editorState オブジェクトで一元管理
 *   - area は派生フィールド(prefecture + municipality から保存時に生成)
 */

import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  setDoc,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { db } from './firebase-init.js';
import { openCsvImportModal } from './p4-csv-import.js';
import { openArchiveModal, openUnarchiveModal } from './p4-archive-modal.js';
import { openMergeModal } from './p4-merge-modal.js';
import { fetchVisitStatsByClient, applyVisitStats } from './client-visit-stats.js';
import { normalizeForSearch, toKatakana } from './text-normalize.js';

// =====================================================================
// 定数
// =====================================================================

const BUSINESS_TYPES = ['物流機器', 'ベジコン', 'その他'];

// 訪問途絶日数の既定値(顧客個別の noVisitDays が未設定のときに使う)
const DEFAULT_NO_VISIT_DAYS = 60;

const SORT_OPTIONS = [
  { value: 'lastVisitDate_desc', label: '最終訪問日(新しい順)' },
  { value: 'name_asc',           label: '名前(あいうえお順)' },
  { value: 'visitCount_desc',    label: '訪問回数(多い順)' },
  { value: 'createdAt_desc',     label: '登録日(新しい順)' }
];

const QUICK_FILTERS = [
  { id: 'no_business',     label: '提案種別未設定' },
  { id: 'no_member',       label: '主担当未割当' },
  { id: 'partially_filled', label: '情報不足' },
  { id: 'merge_candidate', label: '統合候補あり' },
  { id: 'single_visit',    label: '1回のみ訪問' },
  { id: 'stale_30d',       label: '訪問途絶' }
];

// =====================================================================
// 画面状態
// =====================================================================
let allClients = [];                 // Firestore から取得した全件
let activeMembersById = new Map();   // memberId → memberData
let currentSessionMember = null;
let selectedClientId = null;

// 統合候補の事前計算結果(nameNormalized → [clientId])
let mergeCandidateMap = new Map();

let filterState = {
  status: 'active',         // 'active' | 'archived' | 'all'
  search: '',
  sort: 'lastVisitDate_desc',
  quickFilters: new Set()   // Set<string> of QUICK_FILTERS[i].id
};

// ----- Phase 2-A-2: 市町村マスタ -----
let municipalitiesData = null;       // { "佐賀県": ["佐賀市", "基山町", ...] }
let prefecturesList = [];            // 都道府県名の配列(長い順にソート、先頭マッチ判定用)
let municipalityFlatIndex = [];      // 逆引き用 [{ pref, muni, displayLabel }]

// ----- Phase 2-A-2: 編集状態 -----
let editorState = {
  mode: 'empty',     // 'empty' | 'view' | 'create'(編集中は常に dirty 管理)
  draft: null,       // 編集中のクライアントデータ(部分)
  original: null,    // 比較用の元データ(編集前のスナップショット)
  dirty: false,      // 未保存変更があるか
  saving: false      // 保存中(ボタンの二重押し防止)
};

// beforeunload リスナのハンドラ参照(剥がす用)
let beforeUnloadHandler = null;

// =====================================================================
// 公開関数:P4 画面を初期化・描画
// =====================================================================
export async function renderClientsPage(container, sessionMember) {
  currentSessionMember = sessionMember;
  selectedClientId = null;
  filterState = {
    status: 'active',
    search: '',
    sort: 'lastVisitDate_desc',
    quickFilters: new Set()
  };
  editorState = {
    mode: 'empty',
    draft: null,
    original: null,
    dirty: false,
    saving: false
  };

  container.innerHTML = `
    <div class="p4-content">
      <div class="p4-header">
        <h1>顧客マスタ管理</h1>
        <div class="p4-header-actions">
          <button id="p4-refresh-btn" type="button" class="btn btn-ghost">更新</button>
          <button id="p4-csv-import-btn" type="button" class="btn btn-ghost p4-btn-csv-import">📥 CSV インポート</button>
          <button id="p4-add-btn" type="button" class="btn btn-primary">+ 新規追加</button>
        </div>
      </div>
      <div id="p4-error" class="p4-error" style="display:none;"></div>
      <div id="p4-success" class="p4-success" style="display:none;"></div>
      <div id="p4-body">
        <div class="p4-loading">読み込み中…</div>
      </div>
    </div>
  `;

  document.getElementById('p4-refresh-btn').addEventListener('click', async () => {
    if (!confirmDiscardIfDirty()) return;
    await loadAndRender();
  });

  document.getElementById('p4-add-btn').addEventListener('click', () => {
    if (!confirmDiscardIfDirty()) return;
    openCreateEditor();
  });

  document.getElementById('p4-csv-import-btn').addEventListener('click', () => {
    if (!confirmDiscardIfDirty()) return;
    openCsvImportModal({
      existingClients: allClients,
      members: activeMembersById,
      currentSessionMember,
      municipalitiesData,
      onComplete: () => {
        // 投入完了後、一覧を再読み込み
        loadAndRender();
      }
    });
  });

  // beforeunload: 未保存変更がある状態でページを離れようとした時の警告
  setupBeforeUnload();

  // 市町村マスタを並行ロード(失敗してもサジェストなしで動かす)
  loadMunicipalities().catch((err) => {
    console.warn('[チバリヨ P4] 市町村マスタのロード失敗:', err);
  });

  await loadAndRender();
}

// =====================================================================
// 市町村マスタのロード
// =====================================================================
async function loadMunicipalities() {
  if (municipalitiesData) return;  // 既にロード済み

  const res = await fetch('/data/municipalities.json');
  if (!res.ok) throw new Error(`municipalities.json HTTP ${res.status}`);
  municipalitiesData = await res.json();

  // 先頭マッチ判定用: 都道府県名を長い順にソート("神奈川県"を"奈良県"より先に判定)
  prefecturesList = Object.keys(municipalitiesData).sort((a, b) => b.length - a.length);

  // 逆引き用フラットインデックス
  municipalityFlatIndex = [];
  for (const pref of Object.keys(municipalitiesData)) {
    for (const muni of municipalitiesData[pref]) {
      municipalityFlatIndex.push({
        pref,
        muni,
        displayLabel: pref + muni   // 「佐賀県基山町」
      });
    }
  }

  console.log(`[チバリヨ P4] 市町村マスタ読み込み完了: ${municipalityFlatIndex.length}件`);
}

// =====================================================================
// データロード + 描画
// =====================================================================
async function loadAndRender() {
  const body = document.getElementById('p4-body');
  body.innerHTML = '<div class="p4-loading">読み込み中…</div>';

  try {
    // clients を全件取得(active=true/false 両方、createdAt 順) + members + 訪問実績集計
    const [cSnap, mSnap, visitStats] = await Promise.all([
      getDocs(query(collection(db, 'clients'), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, 'members')),
      fetchVisitStatsByClient(),
    ]);
    allClients = cSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // 訪問回数/最終訪問日は日報(type==='訪問')から導出した実績で上書き
    // (顧客マスタの visitCount/lastVisitDate はライブ更新されないため。
    //  表示時計算のみで保存はしない=保存ペイロードにこれらは含めない)
    applyVisitStats(allClients, visitStats);

    // members を保持(主担当表示用)
    activeMembersById = new Map(
      mSnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])
    );

    // 統合候補の事前計算
    computeMergeCandidates();

    renderShell();
    renderFiltered();
  } catch (err) {
    console.error('[チバリヨ P4] loadAndRender 失敗:', err);
    showError('顧客マスタの読み込みに失敗しました: ' + err.message);
    body.innerHTML = '';
  }
}

// =====================================================================
// 統合候補の事前計算
//   - 同じ正規化キーを持つアクティブな clients を検出
//   - 2件以上のグループだけ Map に格納
//   - Phase 2-A-2 修正: 保存済み nameNormalized ではなく、その場で再正規化して比較
//     (正規化ロジックを変更した時、既存データを再保存しなくても反映されるように)
// =====================================================================
function computeMergeCandidates() {
  const groups = new Map();
  for (const c of allClients) {
    if (c.active === false) continue;
    if (!c.name) continue;
    const key = normalizeClientName(c.name);  // 保存値ではなくその場で計算
    if (!key) continue;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(c.id);
  }
  mergeCandidateMap = new Map();
  for (const [key, ids] of groups) {
    if (ids.length >= 2) {
      mergeCandidateMap.set(key, ids);
    }
  }
}

function hasMergeCandidate(client) {
  if (!client || !client.name) return false;
  const key = normalizeClientName(client.name);
  return Boolean(key && mergeCandidateMap.has(key));
}

// =====================================================================
// シェル描画(統計 / フィルタ / 2ペイン)
// =====================================================================
function renderShell() {
  const body = document.getElementById('p4-body');
  body.innerHTML = `
    ${renderStatsBar()}
    ${renderFilterBar()}
    <div class="p4-main">
      <div class="p4-list-pane">
        <div class="p4-list-header">
          <span><span id="p4-list-count" class="p4-list-count">0</span> 件</span>
          <span id="p4-list-sortinfo"></span>
        </div>
        <div class="p4-list-scroll">
          <ul id="p4-list" class="p4-list"></ul>
        </div>
      </div>
      <div class="p4-detail-pane" id="p4-detail-pane">
        ${renderDetailEmpty()}
      </div>
    </div>
  `;

  attachFilterEvents();
}

function renderStatsBar() {
  const active = allClients.filter((c) => c.active !== false);
  const archived = allClients.filter((c) => c.active === false);
  const noBusiness = active.filter(
    (c) => !Array.isArray(c.businessTypes) || c.businessTypes.length === 0
  ).length;
  const noMember = active.filter((c) => !c.primaryMemberId).length;
  const mergeGroups = mergeCandidateMap.size;

  return `
    <div class="p4-stats">
      <div class="p4-stat">
        <span class="p4-stat-label">アクティブ</span>
        <span class="p4-stat-value">${active.length}</span>
        <span class="p4-stat-label">社</span>
      </div>
      <div class="p4-stat-divider"></div>
      <div class="p4-stat">
        <span class="p4-stat-label">アーカイブ</span>
        <span class="p4-stat-value">${archived.length}</span>
        <span class="p4-stat-label">社</span>
      </div>
      <div class="p4-stat-divider"></div>
      <div class="p4-stat ${noBusiness > 0 ? 'is-warning' : ''}">
        <span class="p4-stat-label">提案種別未設定</span>
        <span class="p4-stat-value">${noBusiness}</span>
        <span class="p4-stat-label">社</span>
      </div>
      <div class="p4-stat ${noMember > 0 ? 'is-warning' : ''}">
        <span class="p4-stat-label">主担当未割当</span>
        <span class="p4-stat-value">${noMember}</span>
        <span class="p4-stat-label">社</span>
      </div>
      <div class="p4-stat ${mergeGroups > 0 ? 'is-coral' : ''}">
        <span class="p4-stat-label">統合候補</span>
        <span class="p4-stat-value">${mergeGroups}</span>
        <span class="p4-stat-label">グループ</span>
      </div>
    </div>
  `;
}

function renderFilterBar() {
  return `
    <div class="p4-filterbar">
      <div class="p4-filterbar-row">
        <div class="p4-search">
          <span class="p4-search-icon">🔍</span>
          <input id="p4-search-input" type="text"
                 placeholder="名前 / カナ / 市町村 / 都道府県で検索"
                 value="${escapeAttr(filterState.search)}">
        </div>
        <div class="p4-segment" role="tablist">
          <button type="button" class="p4-segment-btn ${filterState.status === 'active' ? 'is-active' : ''}" data-status="active">アクティブ</button>
          <button type="button" class="p4-segment-btn ${filterState.status === 'archived' ? 'is-active' : ''}" data-status="archived">アーカイブ</button>
          <button type="button" class="p4-segment-btn ${filterState.status === 'all' ? 'is-active' : ''}" data-status="all">全て</button>
        </div>
        <div class="p4-sort">
          <span>並び順:</span>
          <select id="p4-sort-select">
            ${SORT_OPTIONS.map((o) => `
              <option value="${o.value}" ${filterState.sort === o.value ? 'selected' : ''}>${escapeHtml(o.label)}</option>
            `).join('')}
          </select>
        </div>
      </div>
      <div class="p4-filterbar-row">
        <span class="p4-chips-label">クイックフィルタ:</span>
        <div class="p4-chips" id="p4-quick-chips">
          ${QUICK_FILTERS.map((f) => {
            const isActive = filterState.quickFilters.has(f.id);
            return `
              <button type="button"
                      class="p4-chip ${isActive ? 'is-active' : ''}"
                      data-quickfilter="${f.id}">
                ${escapeHtml(f.label)}
              </button>
            `;
          }).join('')}
        </div>
      </div>
    </div>
  `;
}

function renderDetailEmpty() {
  return `
    <div class="p4-detail-empty">
      <div class="p4-detail-empty-icon">←</div>
      <div class="p4-detail-empty-text">左の一覧から顧客を選択してください</div>
      <div class="p4-detail-empty-sub">
        または「+ 新規追加」ボタンから顧客を登録できます
      </div>
    </div>
  `;
}

// =====================================================================
// フィルタイベントのバインド
// =====================================================================
function attachFilterEvents() {
  // 検索
  const searchInput = document.getElementById('p4-search-input');
  let searchTimer = null;
  searchInput.addEventListener('input', (e) => {
    if (searchTimer) clearTimeout(searchTimer);
    const value = e.target.value;
    searchTimer = setTimeout(() => {
      filterState.search = value;
      renderFiltered();
    }, 200);
  });

  // 状態セグメント
  document.querySelectorAll('.p4-segment-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      filterState.status = btn.dataset.status;
      document.querySelectorAll('.p4-segment-btn').forEach((b) => {
        b.classList.toggle('is-active', b === btn);
      });
      renderFiltered();
    });
  });

  // ソート
  document.getElementById('p4-sort-select').addEventListener('change', (e) => {
    filterState.sort = e.target.value;
    renderFiltered();
  });

  // クイックフィルタ
  document.querySelectorAll('#p4-quick-chips .p4-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const id = chip.dataset.quickfilter;
      if (filterState.quickFilters.has(id)) {
        filterState.quickFilters.delete(id);
        chip.classList.remove('is-active');
      } else {
        filterState.quickFilters.add(id);
        chip.classList.add('is-active');
      }
      renderFiltered();
    });
  });
}

// =====================================================================
// フィルタリング + ソート + 一覧描画
// =====================================================================
function renderFiltered() {
  const filtered = applyFilters(allClients);
  const sorted = applySort(filtered);
  renderList(sorted);
  updateListCount(sorted.length);
}

function applyFilters(list) {
  const { status, search, quickFilters } = filterState;
  const now = new Date();

  return list.filter((c) => {
    // 状態
    if (status === 'active' && c.active === false) return false;
    if (status === 'archived' && c.active !== false) return false;
    // 'all' は素通り

    // 検索(部分一致、ケースインセンシティブ)
    if (search.trim()) {
      const needle = normalizeForSearch(search);
      const haystack = normalizeForSearch([
        c.name,
        c.nameKana,
        c.nameNormalized,
        c.area,
        c.prefecture,
        c.municipality
      ].filter(Boolean).join(' '));
      if (!haystack.includes(needle)) return false;
    }

    // クイックフィルタ(AND結合)
    for (const fid of quickFilters) {
      if (fid === 'no_business') {
        if (Array.isArray(c.businessTypes) && c.businessTypes.length > 0) return false;
      } else if (fid === 'no_member') {
        if (c.primaryMemberId) return false;
      } else if (fid === 'partially_filled') {
        if (!c.isPartiallyFilled) return false;
      } else if (fid === 'merge_candidate') {
        if (!hasMergeCandidate(c)) return false;
      } else if (fid === 'single_visit') {
        if ((c.visitCount || 0) !== 1) return false;
      } else if (fid === 'stale_30d') {
        // 顧客個別の訪問途絶日数(未設定なら既定60日)で判定
        const threshold = resolveNoVisitDays(c);
        const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - threshold);
        if (!c.lastVisitDate) return false;
        const d = new Date(c.lastVisitDate);
        if (isNaN(d.getTime())) return false;
        if (d >= cutoff) return false;
      }
    }

    return true;
  });
}

function applySort(list) {
  const sorted = [...list];
  const { sort } = filterState;

  if (sort === 'lastVisitDate_desc') {
    sorted.sort((a, b) => {
      const av = a.lastVisitDate || '';
      const bv = b.lastVisitDate || '';
      if (av === bv) return (a.name || '').localeCompare(b.name || '', 'ja');
      return av < bv ? 1 : -1;  // 降順
    });
  } else if (sort === 'name_asc') {
    sorted.sort((a, b) => (a.nameKana || a.name || '').localeCompare(b.nameKana || b.name || '', 'ja'));
  } else if (sort === 'visitCount_desc') {
    sorted.sort((a, b) => {
      const diff = (b.visitCount || 0) - (a.visitCount || 0);
      if (diff !== 0) return diff;
      return (a.name || '').localeCompare(b.name || '', 'ja');
    });
  } else if (sort === 'createdAt_desc') {
    sorted.sort((a, b) => {
      const av = a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : 0;
      const bv = b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : 0;
      return bv - av;
    });
  }

  // 統合候補フィルタ ON 時はグループ化して並べ直す
  //   - 同じ正規化キーの顧客が連続表示されるように
  //   - グループ内の並びは上記の primary sort を維持
  //   - グループ間の並び: グループサイズ降順 → 先頭メンバーの primary sort 順
  if (filterState.quickFilters.has('merge_candidate')) {
    return groupByMergeKey(sorted);
  }

  return sorted;
}

function groupByMergeKey(sortedList) {
  const groups = new Map();
  const noKey = [];

  for (const c of sortedList) {
    const key = normalizeClientName(c.name || '');
    if (!key) {
      noKey.push(c);
      continue;
    }
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(c);
  }

  // グループキーの並び順: サイズ降順 → 先頭メンバーの現在のソート順位
  const positionOf = new Map();
  sortedList.forEach((c, idx) => { positionOf.set(c.id, idx); });

  const orderedKeys = [...groups.keys()].sort((a, b) => {
    const sizeA = groups.get(a).length;
    const sizeB = groups.get(b).length;
    if (sizeA !== sizeB) return sizeB - sizeA;
    const firstA = groups.get(a)[0];
    const firstB = groups.get(b)[0];
    return (positionOf.get(firstA.id) ?? 0) - (positionOf.get(firstB.id) ?? 0);
  });

  const result = [];
  for (const k of orderedKeys) result.push(...groups.get(k));
  result.push(...noKey);
  return result;
}

function renderList(list) {
  const ul = document.getElementById('p4-list');
  if (!ul) return;

  if (list.length === 0) {
    ul.innerHTML = `
      <li class="p4-list-empty">
        該当する顧客がありません。<br>
        検索条件やフィルタを変更してください。
      </li>
    `;
    return;
  }

  ul.innerHTML = list.map(renderCard).join('');

  // カードクリックイベント
  ul.querySelectorAll('.p4-card').forEach((card) => {
    card.addEventListener('click', () => {
      const newId = card.dataset.clientId;
      if (newId === selectedClientId) return;  // 同じカードなら何もしない
      if (!confirmDiscardIfDirty()) return;

      selectedClientId = newId;
      ul.querySelectorAll('.p4-card').forEach((c) => {
        c.classList.toggle('is-selected', c === card);
      });
      openEditor(selectedClientId);
    });
  });
}

function renderCard(c) {
  const isSelected = c.id === selectedClientId;
  const isArchived = c.active === false;
  const area = c.area || '';
  const visitCount = c.visitCount || 0;

  const badges = [];
  if (isArchived) {
    badges.push(`<span class="p4-card-badge p4-badge-archived">アーカイブ</span>`);
  }
  if (!Array.isArray(c.businessTypes) || c.businessTypes.length === 0) {
    badges.push(`<span class="p4-card-badge p4-badge-warning">提案種別未設定</span>`);
  }
  if (!c.primaryMemberId) {
    badges.push(`<span class="p4-card-badge p4-badge-warning">主担当未割当</span>`);
  }
  if (c.isPartiallyFilled) {
    badges.push(`<span class="p4-card-badge p4-badge-warning">情報不足</span>`);
  }
  if (!isArchived && hasMergeCandidate(c)) {
    const key = normalizeClientName(c.name);
    const groupSize = mergeCandidateMap.get(key).length;
    badges.push(`<span class="p4-card-badge p4-badge-coral">統合候補${groupSize}件</span>`);
  }

  return `
    <li class="p4-card ${isSelected ? 'is-selected' : ''} ${isArchived ? 'is-archived' : ''}"
        data-client-id="${escapeAttr(c.id)}">
      <div class="p4-card-line1">
        <span class="p4-card-name">${escapeHtml(c.name || '(名前未設定)')}</span>
        ${c.nameKana ? `<span class="p4-card-kana">${escapeHtml(c.nameKana)}</span>` : ''}
      </div>
      <div class="p4-card-line2">
        <span class="p4-card-area ${area ? '' : 'is-empty'}">${area ? escapeHtml(area) : '所在地未設定'}</span>
        <span class="p4-card-visit">訪問 ${visitCount}回</span>
      </div>
      ${badges.length > 0 ? `<div class="p4-card-badges">${badges.join('')}</div>` : ''}
    </li>
  `;
}

function updateListCount(n) {
  const el = document.getElementById('p4-list-count');
  if (el) el.textContent = String(n);
}

// =====================================================================
// ===== Phase 2-A-2: 編集フォーム / 新規追加モーダル =====================
// =====================================================================

// =====================================================================
// 編集フォーム:既存クライアントを開く
// =====================================================================
function openEditor(clientId) {
  const client = allClients.find((c) => c.id === clientId);
  if (!client) {
    editorState.mode = 'empty';
    editorState.draft = null;
    editorState.original = null;
    editorState.dirty = false;
    renderDetailEmptyPane();
    return;
  }

  editorState.mode = 'view';
  editorState.original = snapshotClient(client);
  editorState.draft = snapshotClient(client);
  editorState.dirty = false;
  editorState.saving = false;

  renderEditor();
}

// =====================================================================
// 編集フォーム:新規作成モード
// =====================================================================
function openCreateEditor() {
  selectedClientId = null;
  // 一覧の選択状態を解除
  document.querySelectorAll('.p4-card').forEach((c) => c.classList.remove('is-selected'));

  editorState.mode = 'create';
  editorState.original = makeEmptyDraft();
  editorState.draft = makeEmptyDraft();
  editorState.dirty = false;
  editorState.saving = false;

  renderEditor();

  // 名前欄にフォーカス
  setTimeout(() => {
    const nameInput = document.getElementById('p4-edit-name');
    if (nameInput) nameInput.focus();
  }, 50);
}

function makeEmptyDraft() {
  return {
    id: null,
    name: '',
    nameKana: '',
    prefecture: '',
    municipality: '',
    category: '',
    businessTypes: [],
    primaryMemberId: '',
    noVisitDays: null,
    notes: '',
    active: true,
    // 表示用(読み取り専用)
    visitCount: 0,
    lastVisitDate: null
  };
}

// 既存クライアントから編集ドラフト形式へ
function snapshotClient(c) {
  return {
    id: c.id,
    name: c.name || '',
    nameKana: c.nameKana || '',
    prefecture: c.prefecture || '',
    municipality: c.municipality || '',
    category: c.category || '',
    businessTypes: Array.isArray(c.businessTypes) ? [...c.businessTypes] : [],
    primaryMemberId: c.primaryMemberId || '',
    noVisitDays: (typeof c.noVisitDays === 'number' && c.noVisitDays > 0) ? c.noVisitDays : null,
    notes: c.notes || '',
    active: c.active !== false,
    visitCount: c.visitCount || 0,
    lastVisitDate: c.lastVisitDate || null,
    archivedReason: c.archivedReason || '',
    nameNormalized: c.nameNormalized || ''
  };
}

// =====================================================================
// 編集フォーム描画
// =====================================================================
function renderEditor() {
  const pane = document.getElementById('p4-detail-pane');
  if (!pane) return;

  const d = editorState.draft;
  const isCreate = editorState.mode === 'create';
  const isArchived = d.active === false;

  // members を sales / admin に絞ってプルダウン用に
  const memberOptions = [...activeMembersById.values()]
    .filter((m) => m.active !== false && m.chibariyoAccess !== false)
    .filter((m) => m.role === 'sales' || m.role === 'admin')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  pane.innerHTML = `
    <div class="p4-editor ${isArchived ? 'is-archived' : ''}">
      <div class="p4-editor-header">
        <h2 class="p4-editor-title">
          ${isCreate ? '新規顧客の追加' : escapeHtml(d.name || '(名前未設定)')}
          ${isArchived ? '<span class="p4-editor-archived-tag">アーカイブ済み</span>' : ''}
        </h2>
        ${!isCreate && !isArchived ? `
          <div class="p4-editor-meta">
            訪問 ${d.visitCount}回 / 最終訪問: ${d.lastVisitDate || '<span class="p4-text-muted">なし</span>'}
          </div>
        ` : ''}
      </div>

      ${isArchived ? `
        <div class="p4-editor-archived-banner">
          この顧客はアーカイブされています。<br>
          ${d.archivedReason ? `<strong>理由:</strong> ${escapeHtml(d.archivedReason)}` : ''}
        </div>
      ` : ''}

      <form id="p4-edit-form" class="p4-form" autocomplete="off">
        <div class="p4-form-row">
          <label class="p4-form-label" for="p4-edit-name">
            顧客名 <span class="p4-required">*</span>
          </label>
          <input id="p4-edit-name" type="text" class="p4-input"
                 value="${escapeAttr(d.name)}"
                 maxlength="100"
                 ${isArchived ? 'disabled' : ''}>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label" for="p4-edit-kana">
            カナ <span class="p4-required">*</span>
          </label>
          <input id="p4-edit-kana" type="text" class="p4-input"
                 value="${escapeAttr(d.nameKana)}"
                 placeholder="カタカナで入力"
                 maxlength="100"
                 ${isArchived ? 'disabled' : ''}>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label">所在地</label>
          <div class="p4-form-location">
            <div class="p4-location-field">
              <input id="p4-edit-prefecture" type="text" class="p4-input"
                     list="p4-prefecture-list"
                     value="${escapeAttr(d.prefecture)}"
                     placeholder="都道府県"
                     ${isArchived ? 'disabled' : ''}>
              <datalist id="p4-prefecture-list">
                ${prefecturesList.slice().sort().map((p) => `<option value="${escapeAttr(p)}"></option>`).join('')}
              </datalist>
            </div>
            <div class="p4-location-field">
              <input id="p4-edit-municipality" type="text" class="p4-input"
                     list="p4-municipality-list"
                     value="${escapeAttr(d.municipality)}"
                     placeholder="市区町村"
                     ${isArchived ? 'disabled' : ''}>
              <datalist id="p4-municipality-list"></datalist>
            </div>
          </div>
          <div class="p4-form-help">
            都道府県名から市町村名まで一度に入力できます(例:「佐賀県基山町」)
          </div>
          <div id="p4-area-preview" class="p4-area-preview">
            ${d.prefecture || d.municipality
              ? `表示: <strong>${escapeHtml(generateArea(d.prefecture, d.municipality))}</strong>`
              : ''}
          </div>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label">提案種別</label>
          <div class="p4-checkbox-group">
            ${BUSINESS_TYPES.map((bt) => `
              <label class="p4-checkbox-label">
                <input type="checkbox" class="p4-edit-businesstype"
                       value="${escapeAttr(bt)}"
                       ${d.businessTypes.includes(bt) ? 'checked' : ''}
                       ${isArchived ? 'disabled' : ''}>
                <span>${escapeHtml(bt)}</span>
              </label>
            `).join('')}
          </div>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label" for="p4-edit-primary">主担当</label>
          <select id="p4-edit-primary" class="p4-input" ${isArchived ? 'disabled' : ''}>
            <option value="">(未割当)</option>
            ${memberOptions.map((m) => `
              <option value="${escapeAttr(m.id)}" ${d.primaryMemberId === m.id ? 'selected' : ''}>
                ${escapeHtml(m.name)}
              </option>
            `).join('')}
          </select>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label" for="p4-edit-category">カテゴリ</label>
          <input id="p4-edit-category" type="text" class="p4-input"
                 value="${escapeAttr(d.category)}"
                 placeholder="自由入力(任意)"
                 maxlength="50"
                 ${isArchived ? 'disabled' : ''}>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label" for="p4-edit-novisitdays">訪問途絶日数</label>
          <input id="p4-edit-novisitdays" type="number" class="p4-input"
                 value="${d.noVisitDays != null ? escapeAttr(String(d.noVisitDays)) : ''}"
                 min="1" max="365" step="1"
                 ${isArchived ? 'disabled' : ''}>
          <div class="p4-form-help">
            最終訪問からこの日数以上経過すると「訪問途絶」として表示します(空欄の場合は${DEFAULT_NO_VISIT_DAYS}日)
          </div>
        </div>

        <div class="p4-form-row">
          <label class="p4-form-label" for="p4-edit-notes">備考</label>
          <textarea id="p4-edit-notes" class="p4-textarea" rows="3"
                    placeholder="任意のメモ"
                    maxlength="1000"
                    ${isArchived ? 'disabled' : ''}>${escapeHtml(d.notes)}</textarea>
        </div>

        <div class="p4-form-actions">
          ${isArchived ? `
            <div class="p4-text-muted" style="flex:1; font-size:13px;">
              アーカイブ済みの顧客は編集できません。復活させると編集可能になります。
            </div>
            <button type="button" id="p4-edit-unarchive" class="btn btn-primary">
              ↩︎ 復活
            </button>
            <span id="p4-edit-status" class="p4-edit-status"></span>
          ` : `
            <button type="button" id="p4-edit-cancel" class="btn btn-ghost">キャンセル</button>
            <button type="button" id="p4-edit-save" class="btn btn-primary" disabled>
              ${isCreate ? '登録' : '保存'}
            </button>
            ${!isCreate ? `
              <button type="button" id="p4-edit-archive" class="btn btn-ghost p4-btn-archive">
                アーカイブ
              </button>
            ` : ''}
            <span id="p4-edit-status" class="p4-edit-status"></span>
          `}
        </div>
      </form>

      ${!isCreate && !isArchived && hasMergeCandidateByName(d.name) ? renderMergeBanner(d) : ''}
    </div>
  `;

  if (!isArchived) {
    attachEditorEvents();
  } else {
    // アーカイブ済み: 復活ボタンのみイベントバインド
    const unarchiveBtn = document.getElementById('p4-edit-unarchive');
    if (unarchiveBtn) {
      unarchiveBtn.addEventListener('click', unarchiveCurrentClient);
    }
  }
}

// 統合候補チェック(編集中ドラフトの name 用)
function hasMergeCandidateByName(name) {
  if (!name) return false;
  const key = normalizeClientName(name);
  const group = mergeCandidateMap.get(key);
  return Boolean(group && group.length >= 2);
}

// 統合候補の顧客一覧を返す(自分自身を除外)
function getMergeCandidates(name, excludeId) {
  if (!name) return [];
  const key = normalizeClientName(name);
  const group = mergeCandidateMap.get(key);
  if (!group) return [];
  return group
    .filter((id) => id !== excludeId)
    .map((id) => allClients.find((c) => c.id === id))
    .filter(Boolean);
}

// 統合候補バナー描画(編集ペイン内、候補一覧クリックで切替可)
function renderMergeBanner(d) {
  const candidates = getMergeCandidates(d.name, d.id);
  if (candidates.length === 0) return '';

  return `
    <div class="p4-merge-banner">
      <div class="p4-merge-banner-header">
        <strong>統合候補 ${candidates.length}件</strong>
        <span class="p4-merge-banner-hint">同じ正規化名を持つ顧客があります(クリックで切替)</span>
      </div>
      <ul class="p4-merge-candidate-list">
        ${candidates.map((c) => {
          const area = c.area || '';
          const member = c.primaryMemberId ? activeMembersById.get(c.primaryMemberId) : null;
          const memberName = member ? member.name : '';
          const visitCount = c.visitCount || 0;
          return `
            <li class="p4-merge-candidate"
                data-candidate-id="${escapeAttr(c.id)}"
                role="button"
                tabindex="0">
              <div class="p4-merge-candidate-line1">
                <span class="p4-merge-candidate-name">${escapeHtml(c.name || '(名前未設定)')}</span>
                ${c.nameKana ? `<span class="p4-merge-candidate-kana">${escapeHtml(c.nameKana)}</span>` : ''}
              </div>
              <div class="p4-merge-candidate-line2">
                <span class="p4-merge-candidate-area ${area ? '' : 'is-empty'}">${area ? escapeHtml(area) : '所在地未設定'}</span>
                ${memberName ? `<span class="p4-merge-candidate-member">担当: ${escapeHtml(memberName)}</span>` : '<span class="p4-merge-candidate-member is-empty">担当未割当</span>'}
                <span class="p4-merge-candidate-visit">訪問 ${visitCount}回</span>
              </div>
            </li>
          `;
        }).join('')}
      </ul>
      <div class="p4-merge-banner-action">
        <button type="button" id="p4-merge-start" class="p4-merge-start-btn">
          🔗 重複を統合する…
        </button>
      </div>
    </div>
  `;
}

// =====================================================================
// 編集フォームのイベントバインド
// =====================================================================
function attachEditorEvents() {
  const form = document.getElementById('p4-edit-form');
  if (!form) return;

  // 各フィールド → ドラフト更新
  const nameInput = document.getElementById('p4-edit-name');
  nameInput.addEventListener('input', (e) => {
    editorState.draft.name = e.target.value;
    markDirty();
  });

  document.getElementById('p4-edit-kana').addEventListener('input', (e) => {
    editorState.draft.nameKana = e.target.value;
    markDirty();
  });

  const prefInput = document.getElementById('p4-edit-prefecture');
  const muniInput = document.getElementById('p4-edit-municipality');

  prefInput.addEventListener('input', (e) => {
    handlePrefectureInput(e.target.value);
  });

  muniInput.addEventListener('input', (e) => {
    handleMunicipalityInput(e.target.value);
  });

  // 提案種別チェックボックス
  document.querySelectorAll('.p4-edit-businesstype').forEach((cb) => {
    cb.addEventListener('change', () => {
      editorState.draft.businessTypes = Array.from(
        document.querySelectorAll('.p4-edit-businesstype:checked')
      ).map((c) => c.value);
      markDirty();
    });
  });

  // 主担当
  document.getElementById('p4-edit-primary').addEventListener('change', (e) => {
    editorState.draft.primaryMemberId = e.target.value;
    markDirty();
  });

  // カテゴリ
  document.getElementById('p4-edit-category').addEventListener('input', (e) => {
    editorState.draft.category = e.target.value;
    markDirty();
  });

  // 訪問途絶日数
  document.getElementById('p4-edit-novisitdays').addEventListener('input', (e) => {
    editorState.draft.noVisitDays = parseNoVisitDays(e.target.value);
    markDirty();
  });

  // 備考
  document.getElementById('p4-edit-notes').addEventListener('input', (e) => {
    editorState.draft.notes = e.target.value;
    markDirty();
  });

  // 保存
  document.getElementById('p4-edit-save').addEventListener('click', () => {
    saveDraft();
  });

  // キャンセル
  document.getElementById('p4-edit-cancel').addEventListener('click', () => {
    if (!confirmDiscardIfDirty('編集内容を破棄してよろしいですか?')) return;
    cancelEdit();
  });

  // アーカイブ(既存顧客のみ、新規モードでは要素なし)
  const archiveBtn = document.getElementById('p4-edit-archive');
  if (archiveBtn) {
    archiveBtn.addEventListener('click', () => {
      archiveCurrentClient();
    });
  }

  // 統合候補のクリック → 候補顧客へ切替
  document.querySelectorAll('.p4-merge-candidate').forEach((el) => {
    const switchTo = () => {
      const candidateId = el.dataset.candidateId;
      if (!candidateId) return;
      if (!confirmDiscardIfDirty()) return;

      selectedClientId = candidateId;
      // 一覧側の選択状態を同期(該当カードが一覧に表示されている場合のみ)
      document.querySelectorAll('.p4-card').forEach((c) => {
        c.classList.toggle('is-selected', c.dataset.clientId === candidateId);
      });
      openEditor(candidateId);
      // 該当カードまでスクロール(表示されていれば)
      setTimeout(() => {
        const card = document.querySelector(`.p4-card[data-client-id="${candidateId}"]`);
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    };
    el.addEventListener('click', switchTo);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        switchTo();
      }
    });
  });

  // 統合開始ボタン → 統合モーダル
  const mergeStartBtn = document.getElementById('p4-merge-start');
  if (mergeStartBtn) {
    mergeStartBtn.addEventListener('click', () => {
      if (!confirmDiscardIfDirty('編集内容を破棄して統合に進みますか?')) return;
      openMergeForCurrent();
    });
  }

  // 初期サジェスト構築(prefecture が既に入っているなら)
  refreshMunicipalitySuggestions();
}

// 編集中の顧客が属する重複グループ全体を統合モーダルに渡す
function openMergeForCurrent() {
  const d = editorState.draft;
  if (!d || !d.id) return;
  const key = normalizeClientName(d.name);
  const ids = mergeCandidateMap.get(key);
  if (!ids || ids.length < 2) return;

  const group = ids
    .map((id) => allClients.find((c) => c.id === id))
    .filter(Boolean);
  if (group.length < 2) return;

  openMergeModal({
    group,
    currentClientId: d.id,
    currentSessionMember,
    membersById: activeMembersById,
    onComplete: async () => {
      // 統合後は付け替え・アーカイブが起きているので一覧を再読込し、
      // 主(残った顧客)を選択状態にして編集ペインを開き直す。
      selectedClientId = d.id;
      await loadAndRender();
      if (allClients.some((c) => c.id === d.id)) {
        openEditor(d.id);
        setTimeout(() => {
          const card = document.querySelector(`.p4-card[data-client-id="${d.id}"]`);
          if (card) {
            card.classList.add('is-selected');
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    },
  });
}

// =====================================================================
// prefecture 入力ハンドラ
// =====================================================================
function handlePrefectureInput(value) {
  // 完全一致を許す(都道府県名としてのみ有効、未マッチなら空扱いはせず保持はする)
  editorState.draft.prefecture = value.trim();
  refreshMunicipalitySuggestions();
  refreshAreaPreview();
  markDirty();
}

// =====================================================================
// municipality 入力ハンドラ(緩やかモード・案Q の核)
// =====================================================================
function handleMunicipalityInput(value) {
  // datalist から「佐賀県基山町」のように選ばれた可能性をチェック
  // (案Q: 接続詞なし、都道府県名で始まる場合は分割する)

  if (municipalitiesData && value) {
    // 先頭マッチで都道府県を切り出す(prefecturesList は長い順なので「神奈川県」が「奈良県」より先)
    for (const pref of prefecturesList) {
      if (value.startsWith(pref) && value.length > pref.length) {
        const rest = value.substring(pref.length);
        // rest がその県の市町村に含まれるなら分割確定
        if (municipalitiesData[pref] && municipalitiesData[pref].includes(rest)) {
          editorState.draft.prefecture = pref;
          editorState.draft.municipality = rest;
          // フィールドの表示も同期
          const prefInput = document.getElementById('p4-edit-prefecture');
          const muniInput = document.getElementById('p4-edit-municipality');
          if (prefInput) prefInput.value = pref;
          if (muniInput) muniInput.value = rest;
          refreshMunicipalitySuggestions();
          refreshAreaPreview();
          markDirty();
          return;
        }
      }
    }
  }

  // 通常の入力(分割対象でなければそのまま保持)
  editorState.draft.municipality = value.trim();
  refreshMunicipalitySuggestions();
  refreshAreaPreview();
  markDirty();
}

// =====================================================================
// municipality サジェストの datalist を再構築
//   - prefecture 選択済: その県の市町村のみ
//   - prefecture 未選択: 全国フラット("佐賀県基山町"形式)
// =====================================================================
function refreshMunicipalitySuggestions() {
  const list = document.getElementById('p4-municipality-list');
  if (!list || !municipalitiesData) return;

  const pref = editorState.draft.prefecture;
  let options = '';

  if (pref && municipalitiesData[pref]) {
    // 県確定モード:市町村名のみ
    options = municipalitiesData[pref]
      .map((m) => `<option value="${escapeAttr(m)}"></option>`)
      .join('');
  } else {
    // フラットモード:県名+市町村名
    // 全1741件入れると重いので、現在の入力値で前方/部分一致を絞る(なければ非表示)
    const muniInput = document.getElementById('p4-edit-municipality');
    const needle = (muniInput?.value || '').trim();

    if (needle.length === 0) {
      // 何も入力していない時は空(datalist 全件を出すとブラウザが重くなる)
      options = '';
    } else {
      // マッチ判定:市町村名に対して部分一致
      const matched = municipalityFlatIndex.filter((item) => item.muni.includes(needle));
      // 並び:完全一致 > 前方一致 > 部分一致、同位なら県のあいうえお順
      matched.sort((a, b) => {
        const score = (item) => {
          if (item.muni === needle) return 0;
          if (item.muni.startsWith(needle)) return 1;
          return 2;
        };
        const sa = score(a);
        const sb = score(b);
        if (sa !== sb) return sa - sb;
        return a.pref.localeCompare(b.pref, 'ja');
      });
      options = matched.slice(0, 30)
        .map((item) => `<option value="${escapeAttr(item.displayLabel)}"></option>`)
        .join('');
    }
  }

  list.innerHTML = options;
}

// =====================================================================
// area プレビューを更新
// =====================================================================
function refreshAreaPreview() {
  const el = document.getElementById('p4-area-preview');
  if (!el) return;

  const pref = editorState.draft.prefecture;
  const muni = editorState.draft.municipality;

  if (!pref && !muni) {
    el.innerHTML = '';
    return;
  }

  const area = generateArea(pref, muni);
  el.innerHTML = `表示: <strong>${escapeHtml(area)}</strong>`;
}

// =====================================================================
// area 生成ロジック
//   - prefecture と municipality を単純結合
//   - 「熊本市」のような政令市と県名が重複するケースも「熊本県熊本市」と表示
//     (v6 第5節の特別ルールを廃止 / 2026-05-27 仕様変更)
// =====================================================================
function generateArea(prefecture, municipality) {
  if (!prefecture && !municipality) return '';
  if (!municipality) return prefecture;
  if (!prefecture) return municipality;
  return prefecture + municipality;
}

// =====================================================================
// 訪問途絶日数の解決
//   - 顧客個別の noVisitDays が正の数なら採用、未設定なら既定 DEFAULT_NO_VISIT_DAYS
// =====================================================================
function resolveNoVisitDays(client) {
  const v = client && client.noVisitDays;
  return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : DEFAULT_NO_VISIT_DAYS;
}

// 入力文字列を訪問途絶日数(正の整数 or null)へ正規化
function parseNoVisitDays(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  const n = Math.floor(Number(s));
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// =====================================================================
// 変更を検知してダーティフラグと保存ボタン状態を更新
// =====================================================================
function markDirty() {
  const isDirty = !deepEqualDraft(editorState.draft, editorState.original);
  editorState.dirty = isDirty;

  const saveBtn = document.getElementById('p4-edit-save');
  if (saveBtn) saveBtn.disabled = !isDirty || editorState.saving;
}

// 編集ドラフトの比較(深い等価チェック・必要なフィールドだけ)
function deepEqualDraft(a, b) {
  if (!a || !b) return a === b;
  const fields = ['name', 'nameKana', 'prefecture', 'municipality',
                  'category', 'primaryMemberId', 'notes'];
  for (const f of fields) {
    if ((a[f] || '') !== (b[f] || '')) return false;
  }
  // noVisitDays は number | null
  if ((a.noVisitDays ?? null) !== (b.noVisitDays ?? null)) return false;
  // businessTypes 配列比較
  const ab = [...(a.businessTypes || [])].sort();
  const bb = [...(b.businessTypes || [])].sort();
  if (ab.length !== bb.length) return false;
  for (let i = 0; i < ab.length; i++) {
    if (ab[i] !== bb[i]) return false;
  }
  return true;
}

// =====================================================================
// バリデーション
// =====================================================================
function validateDraft(draft) {
  const errors = [];

  // 顧客名は必須
  if (!draft.name || !draft.name.trim()) {
    errors.push('顧客名を入力してください');
  } else if (draft.name.length > 100) {
    errors.push('顧客名は100文字以内で入力してください');
  }

  // カナ必須・100文字以内
  if (!draft.nameKana || !draft.nameKana.trim()) {
    errors.push('カナを入力してください');
  } else if (draft.nameKana.length > 100) {
    errors.push('カナは100文字以内で入力してください');
  }

  // prefecture が空でない時は、マスタに存在することを確認(警告レベル、空はOK)
  if (draft.prefecture && municipalitiesData && !municipalitiesData[draft.prefecture]) {
    errors.push(`都道府県名「${draft.prefecture}」が認識できません。一覧から選択してください`);
  }

  // municipality が空でない時、prefecture も存在し、そこに含まれることを確認
  if (draft.municipality && draft.prefecture && municipalitiesData &&
      municipalitiesData[draft.prefecture] &&
      !municipalitiesData[draft.prefecture].includes(draft.municipality)) {
    errors.push(`「${draft.municipality}」は「${draft.prefecture}」の市町村として見つかりません`);
  }

  // notes
  if (draft.notes && draft.notes.length > 1000) {
    errors.push('備考は1000文字以内で入力してください');
  }

  // 訪問途絶日数(任意・1〜365)
  if (draft.noVisitDays != null && (draft.noVisitDays < 1 || draft.noVisitDays > 365)) {
    errors.push('訪問途絶日数は1〜365の範囲で入力してください');
  }

  return errors;
}

// =====================================================================
// 保存処理(新規 / 更新 共通)
// =====================================================================
async function saveDraft() {
  if (editorState.saving) return;

  const draft = editorState.draft;
  const errors = validateDraft(draft);
  if (errors.length > 0) {
    setEditStatus(errors.join(' / '), 'error');
    return;
  }

  editorState.saving = true;
  document.getElementById('p4-edit-save').disabled = true;
  setEditStatus('保存中…', 'info');

  try {
    const isCreate = editorState.mode === 'create';
    const clientId = isCreate ? generateClientId() : draft.id;

    // 保存するペイロード
    const area = generateArea(draft.prefecture, draft.municipality);
    const nameNormalized = normalizeClientName(draft.name);
    const isPartiallyFilled = computeIsPartiallyFilled(draft);

    const baseData = {
      name: draft.name.trim(),
      nameKana: toKatakana(draft.nameKana),
      nameNormalized,
      prefecture: (draft.prefecture || '').trim(),
      municipality: (draft.municipality || '').trim(),
      area,
      category: (draft.category || '').trim(),
      businessTypes: Array.isArray(draft.businessTypes) ? [...draft.businessTypes] : [],
      primaryMemberId: draft.primaryMemberId || null,
      noVisitDays: draft.noVisitDays != null ? draft.noVisitDays : null,
      notes: (draft.notes || '').trim(),
      isPartiallyFilled,
      updatedAt: serverTimestamp()
    };

    if (isCreate) {
      // 新規作成時の追加フィールド
      baseData.active = true;
      baseData.visitCount = 0;
      baseData.lastVisitDate = null;
      baseData.mergedInto = null;
      baseData.archivedAt = null;
      baseData.archivedBy = null;
      baseData.archivedReason = '';
      baseData.createdAt = serverTimestamp();
      baseData.createdBy = currentSessionMember?.id || null;
    }

    await setDoc(doc(db, 'clients', clientId), baseData, { merge: true });

    // 成功 → ローカル状態を更新
    setEditStatus(isCreate ? '登録しました' : '保存しました', 'success');

    // 一覧データを再取得して再描画
    await loadAndRender();

    // 新規作成の場合、作成したクライアントを選択状態に
    if (isCreate) {
      selectedClientId = clientId;
    }

    // エディタを再表示(view モードへ)
    if (selectedClientId) {
      openEditor(selectedClientId);
      // 選択カードへスクロール
      setTimeout(() => {
        const card = document.querySelector(`.p4-card[data-client-id="${selectedClientId}"]`);
        if (card) {
          card.classList.add('is-selected');
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  } catch (err) {
    console.error('[チバリヨ P4] 保存失敗:', err);
    setEditStatus('保存に失敗しました: ' + err.message, 'error');
    editorState.saving = false;
    const btn = document.getElementById('p4-edit-save');
    if (btn) btn.disabled = false;
  }
}

// クライアントID生成(タイムスタンプベース、衝突しにくい形式)
function generateClientId() {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 8);
  return `cli_${ts}_${rand}`;
}

// 名前の正規化(統合候補検出用)
//   全角→半角、英字小文字化、空白・記号類を除去、法人格を除去
function normalizeClientName(name) {
  if (!name) return '';

  let s = name
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s\u3000・/／\-―()()「」『』、,.。·]/g, '');

  // 法人格の除去
  //   - 前後どちらに付いていても除去(株式会社高富物産 / 高富物産株式会社 両対応)
  //   - 長いものから順に判定(「一般社団法人」を「社団法人」より先に)
  const LEGAL_PREFIXES = [
    '特定非営利活動法人', '一般社団法人', '公益社団法人', '一般財団法人', '公益財団法人',
    '地方独立行政法人', '独立行政法人', '国立大学法人',
    '社会福祉法人', '医療法人', '学校法人', '宗教法人', '相互会社',
    '社団法人', '財団法人', 'npo法人',
    '農業協同組合', '漁業協同組合', '生活協同組合', '事業協同組合', '協同組合',
    '株式会社', '有限会社', '合同会社', '合資会社', '合名会社',
    '株', '有', '同'  // ㈱㈲㈿ 等の NFKC 後の単独文字
  ];

  // 前方除去
  for (const p of LEGAL_PREFIXES) {
    if (s.startsWith(p)) {
      s = s.substring(p.length);
      break;  // 1つ削れば十分(多重法人格は想定外)
    }
  }

  // 後方除去
  for (const p of LEGAL_PREFIXES) {
    if (s.endsWith(p)) {
      s = s.substring(0, s.length - p.length);
      break;
    }
  }

  return s.trim();
}

// 情報不足フラグの計算
function computeIsPartiallyFilled(draft) {
  if (!draft.nameKana) return true;
  if (!draft.prefecture && !draft.municipality) return true;
  if (!Array.isArray(draft.businessTypes) || draft.businessTypes.length === 0) return true;
  return false;
}

// =====================================================================
// アーカイブ実行(編集中の顧客を active: false 化)
//   - 未保存変更があれば破棄確認
//   - 専用モーダルで理由入力を受け取る(Phase 2-A-3 でモーダル化完了)
//   - Firestore 書き込みはモーダル側で実行
// =====================================================================
async function archiveCurrentClient() {
  if (editorState.saving) return;
  if (editorState.mode !== 'view') return;
  if (!editorState.draft || !editorState.draft.id) return;

  // 未保存変更があれば破棄確認
  if (editorState.dirty) {
    if (!window.confirm('未保存の変更があります。変更を破棄してアーカイブしますか?')) {
      return;
    }
    // 破棄して進む: dirty フラグを下げて以降の確認ダイアログを抑止
    editorState.dirty = false;
  }

  // 元データから client を組み立て(モーダルに渡す)
  const clientForModal = {
    id: editorState.draft.id,
    name: editorState.original?.name || editorState.draft.name || '',
    archivedAt: editorState.original?.archivedAt || null,
    archivedReason: editorState.original?.archivedReason || ''
  };

  openArchiveModal({
    client: clientForModal,
    currentSessionMember,
    onComplete: handlePostArchive
  });
}

// アーカイブ完了後の後処理
async function handlePostArchive() {
  // 一覧を再読み込み
  await loadAndRender();

  // 編集ペインは空状態に戻す
  selectedClientId = null;
  editorState.mode = 'empty';
  editorState.draft = null;
  editorState.original = null;
  editorState.dirty = false;
  editorState.saving = false;
  renderDetailEmptyPane();
}

// =====================================================================
// 復活実行(アーカイブ済み顧客を active: true 化)
//   - 専用モーダルで復活理由入力を受け取る
//   - archive 履歴は保持(監査痕跡)、unarchived* フィールドを追加記録
// =====================================================================
async function unarchiveCurrentClient() {
  if (editorState.saving) return;
  if (editorState.mode !== 'view') return;
  if (!editorState.draft || !editorState.draft.id) return;

  // アーカイブ済みでない場合は何もしない(防御)
  if (editorState.draft.active !== false) return;

  // 元のクライアントデータから完全な情報を取得(archivedAt 等)
  const fullClient = allClients.find((c) => c.id === editorState.draft.id);
  if (!fullClient) return;

  openUnarchiveModal({
    client: fullClient,
    currentSessionMember,
    onComplete: handlePostUnarchive
  });
}

// 復活完了後の後処理
async function handlePostUnarchive() {
  // 一覧を再読み込み
  await loadAndRender();

  // 復活したクライアントを引き続き選択状態にし、編集モードで開き直す
  // (active: true になったので編集可能なフォームが表示される)
  if (selectedClientId) {
    openEditor(selectedClientId);
    setTimeout(() => {
      const card = document.querySelector(`.p4-card[data-client-id="${selectedClientId}"]`);
      if (card) {
        card.classList.add('is-selected');
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }
}

// =====================================================================
// キャンセル / 編集破棄
// =====================================================================
function cancelEdit() {
  if (editorState.mode === 'create') {
    // 新規作成キャンセル → 空状態へ
    editorState.mode = 'empty';
    editorState.draft = null;
    editorState.original = null;
    editorState.dirty = false;
    selectedClientId = null;
    renderDetailEmptyPane();
  } else {
    // 編集キャンセル → 元の状態に戻す
    editorState.draft = snapshotClientFromOriginal();
    editorState.dirty = false;
    renderEditor();
  }
}

function snapshotClientFromOriginal() {
  return { ...editorState.original, businessTypes: [...(editorState.original.businessTypes || [])] };
}

function renderDetailEmptyPane() {
  const pane = document.getElementById('p4-detail-pane');
  if (pane) pane.innerHTML = renderDetailEmpty();
}

// =====================================================================
// 編集ステータス表示
// =====================================================================
function setEditStatus(msg, level) {
  const el = document.getElementById('p4-edit-status');
  if (!el) return;
  el.textContent = msg;
  el.className = 'p4-edit-status is-' + (level || 'info');
  if (level === 'success') {
    setTimeout(() => {
      if (el.textContent === msg) el.textContent = '';
    }, 3000);
  }
}

// =====================================================================
// 未保存変更の確認
//   - dirty なら confirm() で破棄を確認
//   - キャンセルされたら false を返す(呼び出し側で処理を止める)
// =====================================================================
function confirmDiscardIfDirty(message) {
  if (!editorState.dirty) return true;
  const msg = message || '未保存の変更があります。破棄してよろしいですか?';
  return window.confirm(msg);
}

// =====================================================================
// beforeunload リスナのセットアップ
// =====================================================================
function setupBeforeUnload() {
  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
  }
  beforeUnloadHandler = (e) => {
    if (editorState.dirty) {
      e.preventDefault();
      e.returnValue = '';  // モダンブラウザは固定メッセージを表示
      return '';
    }
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);
}

// =====================================================================
// ユーティリティ
// =====================================================================
function showError(msg) {
  const el = document.getElementById('p4-error');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  setTimeout(() => { el.style.display = 'none'; }, 6000);
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

function escapeAttr(str) {
  return escapeHtml(str);
}
